export type WorkoutTemplateDisplay = {
  id: string;
  title: string;
  description: string;
  duration: number;
  difficulty: string;
  difficultyLevels: string[];
  category: string;
  goals: string[];
  equipment: string[];
  exercises: number;
  rating: number;
  muscleGroups: string[];
  estimatedCalories: number;
};

export const WORKOUT_CATEGORIES = ["All", "HIIT", "Strength", "Cardio", "Flexibility", "Core"] as const;
export const WORKOUT_DIFFICULTIES = ["All", "Beginner", "Intermediate", "Advanced", "Expert", "Pro"] as const;
export const WORKOUT_DURATIONS = ["All", "Under 20 min", "20-40 min", "40+ min"] as const;
export const WORKOUT_EXERCISE_COUNTS = ["All", "1-5", "6-10", "11+"] as const;

export type WorkoutSortBy = "default" | "name" | "duration-asc" | "duration-desc" | "exercises";

export const WORKOUT_SORT_OPTIONS: { value: WorkoutSortBy; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "name", label: "Name A–Z" },
  { value: "duration-asc", label: "Duration (shortest)" },
  { value: "duration-desc", label: "Duration (longest)" },
  { value: "exercises", label: "Most exercises" },
];

export type WorkoutLibraryFilterState = {
  selectedCategory: string;
  selectedDifficulty: string;
  selectedDuration: string;
  selectedMuscleGroups: string[];
  selectedExerciseCount: string;
  sortBy: WorkoutSortBy;
};

export type WorkoutLibraryFilters = WorkoutLibraryFilterState & {
  searchQuery: string;
};

export const DEFAULT_WORKOUT_LIBRARY_FILTER_STATE: WorkoutLibraryFilterState = {
  selectedCategory: "All",
  selectedDifficulty: "All",
  selectedDuration: "All",
  selectedMuscleGroups: [],
  selectedExerciseCount: "All",
  sortBy: "default",
};

export function countActiveWorkoutFilters(filters: WorkoutLibraryFilterState): number {
  let count = 0;
  if (filters.selectedCategory !== "All") count++;
  if (filters.selectedDifficulty !== "All") count++;
  if (filters.selectedDuration !== "All") count++;
  if (filters.selectedMuscleGroups.length > 0) count++;
  if (filters.selectedExerciseCount !== "All") count++;
  return count;
}

function matchesExerciseCountBucket(exercises: number, bucket: string): boolean {
  if (bucket === "All") return true;
  if (bucket === "1-5") return exercises >= 1 && exercises <= 5;
  if (bucket === "6-10") return exercises >= 6 && exercises <= 10;
  if (bucket === "11+") return exercises >= 11;
  return true;
}

function sortWorkouts(
  workouts: WorkoutTemplateDisplay[],
  sortBy: WorkoutSortBy
): WorkoutTemplateDisplay[] {
  if (sortBy === "default") return workouts;

  const sorted = [...workouts];
  switch (sortBy) {
    case "name":
      sorted.sort((a, b) => a.title.localeCompare(b.title));
      break;
    case "duration-asc":
      sorted.sort((a, b) => a.duration - b.duration);
      break;
    case "duration-desc":
      sorted.sort((a, b) => b.duration - a.duration);
      break;
    case "exercises":
      sorted.sort((a, b) => b.exercises - a.exercises);
      break;
  }
  return sorted;
}

export function applyWorkoutLibraryFilters(
  templates: WorkoutTemplateDisplay[],
  {
    searchQuery,
    selectedCategory,
    selectedDifficulty,
    selectedDuration,
    selectedMuscleGroups,
    selectedExerciseCount,
    sortBy,
  }: WorkoutLibraryFilters
): WorkoutTemplateDisplay[] {
  const q = searchQuery.trim().toLowerCase();

  const filtered = templates.filter((workout) => {
    const matchesSearch =
      q.length === 0 ||
      workout.title.toLowerCase().includes(q) ||
      workout.description.toLowerCase().includes(q);

    const matchesCategory = selectedCategory === "All" || workout.category === selectedCategory;
    const matchesDifficulty =
      selectedDifficulty === "All" ||
      (workout.difficultyLevels?.length
        ? workout.difficultyLevels.includes(selectedDifficulty)
        : workout.difficulty === selectedDifficulty);

    let matchesDuration = true;
    if (selectedDuration !== "All") {
      const duration = workout.duration;
      if (selectedDuration === "Under 20 min") matchesDuration = duration < 20;
      else if (selectedDuration === "20-40 min") matchesDuration = duration >= 20 && duration <= 40;
      else if (selectedDuration === "40+ min") matchesDuration = duration > 40;
    }

    const matchesMuscles =
      selectedMuscleGroups.length === 0 ||
      (workout.muscleGroups?.length
        ? workout.muscleGroups.some((m) => selectedMuscleGroups.includes(m))
        : false);

    const matchesExerciseCount = matchesExerciseCountBucket(
      workout.exercises,
      selectedExerciseCount
    );

    return (
      matchesSearch &&
      matchesCategory &&
      matchesDifficulty &&
      matchesDuration &&
      matchesMuscles &&
      matchesExerciseCount
    );
  });

  return sortWorkouts(filtered, sortBy);
}
